import { useAppSelector } from '@/redux/hooks'
import { useTranslation } from 'react-i18next'
import { Roles } from '@/utils'
import { ActionButton, AlertTriangle } from '@itsrever/design-system'
import { ButtonGroup, ClickAwayListener, Grow, Popper } from '@mui/material'
import { useParams } from 'react-router'
import { useRef, useState } from 'react'
import { ChevronDownIcon } from '@itsrever/design-system'
import DeclinedIcon from '@/assets/images/icons/DeclinedIcon'
import EditIcon from '@/assets/images/icons/EditIcon'
import { RequestDocumentsModal } from '@/ui/components/request-documents-modal'

interface AuthorizeBannerProps {
  authorizeMode: boolean
  unauthorizeMode: boolean
  setAuthorizeMode: (value: boolean) => void
  setUnauthorizeMode: (value: boolean) => void
  uploadDocsEnabled: boolean
  setEditAmountMode: (value: boolean) => void
  editAmountMode: boolean
}

const AuthorizeBanner: React.FC<AuthorizeBannerProps> = ({
  setAuthorizeMode,
  setUnauthorizeMode,
  uploadDocsEnabled,
  setEditAmountMode
}) => {
  const { t } = useTranslation()
  const { id } = useParams()
  const role = useAppSelector(
    (store) => store.userApi.getMe.response.user?.role
  )

  const authorizeProcess = (isEditAmount?: boolean) => {
    setAuthorizeMode(true)
    setUnauthorizeMode(false)
    if (isEditAmount) {
      setEditAmountMode(true)
    } else {
      setEditAmountMode(false)
    }
    setOpenDeclineMenu(false)
  }
  const unauthorizeProcess = () => {
    setUnauthorizeMode(true)
    setAuthorizeMode(false)
    setEditAmountMode(false)
    setOpenDeclineMenu(false)
  }

  const [showRequestDocsModal, setShowRequestDocsModal] = useState(false)

  const declineMenu = useRef<HTMLDivElement>(null)
  const [openDeclineMenu, setOpenDeclineMenu] = useState(false)

  const handleCloseDecline = (event: Event) => {
    if (
      declineMenu.current &&
      declineMenu.current.contains(event.target as HTMLElement)
    ) {
      return
    }
    setOpenDeclineMenu(false)
  }

  return (
    <>
      <div className="my-4 flex w-full items-center justify-between gap-6 rounded-lg border-t-4 border-solid border-yellow-400 bg-white p-2 shadow-md">
        <div className="flex shrink items-center gap-1.5">
          <AlertTriangle className="size-6 shrink-0 text-yellow-400" />
          <p className="shrink">
            <b>{t('process_details_page.return_request')}:</b>{' '}
            {t('process_details_page.return_request_description')}
          </p>
        </div>
        {(role === Roles.ReadWrite || role === Roles.Reviewer) && (
          <div className="shrink-0">
            <div className="flex gap-2">
              {uploadDocsEnabled && (
                <ActionButton
                  className="mr-2"
                  onClick={() => setShowRequestDocsModal(true)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    className="size-[17px] fill-white"
                  >
                    <path d="M4.5 6.75c0-.69.56-1.25 1.25-1.25h1.514c.473 0 .906.268 1.118.691l.17.342c.297.592.903.967 1.566.967h4.132c.69 0 1.25.56 1.25 1.25 0 .414.336.75.75.75s.75-.336.75-.75c0-1.519-1.231-2.75-2.75-2.75h-4.132c-.095 0-.181-.053-.224-.138l-.17-.342c-.466-.931-1.418-1.52-2.46-1.52h-1.514c-1.519 0-2.75 1.231-2.75 2.75v6.5c0 1.519 1.231 2.75 2.75 2.75h5c.414 0 .75-.336.75-.75s-.336-.75-.75-.75h-5c-.69 0-1.25-.56-1.25-1.25v-6.5Z" />
                    <path d="M15.75 13.31v2.94c0 .414-.336.75-.75.75s-.75-.336-.75-.75v-2.94l-.72.72c-.293.293-.767.293-1.06 0-.293-.293-.293-.767 0-1.06l2-2c.293-.293.767-.293 1.06 0l2 2c.293.293.293.767 0 1.06-.293.293-.767.293-1.06 0l-.72-.72Z" />
                  </svg>
                  <p className="text-sm leading-4">
                    {t('process_details_page.request_docs')}
                  </p>
                </ActionButton>
              )}

              <button
                className={
                  'flex items-center gap-2 rounded-lg border border-solid border-emerald-500 px-4 py-2 text-emerald-500 transition-colors duration-300 ease-in-out hover:bg-emerald-200 '
                }
                onClick={() => authorizeProcess()}
              >
                <p className="flex flex-col text-sm">
                  <span> {t('process_details_page.authorize_accept')}</span>
                </p>
              </button>
              <ButtonGroup ref={declineMenu}>
                <button
                  className={
                    'flex items-center gap-2 rounded-lg border border-red-500 px-4 py-2 text-red-500 transition-colors duration-300 ease-in-out hover:bg-red-200 '
                  }
                  onClick={() => setOpenDeclineMenu(!openDeclineMenu)}
                >
                  <p className="flex flex-col text-sm">
                    <span>{t('process_details_page.authorize_decline')}</span>
                  </p>
                  <ChevronDownIcon className="text-red-500" color={'#ef4444'} />
                </button>
              </ButtonGroup>
              <Popper
                sx={{
                  zIndex: 1
                }}
                open={openDeclineMenu}
                anchorEl={declineMenu.current}
                role={undefined}
                transition
                disablePortal
              >
                {({ TransitionProps, placement }) => (
                  <Grow
                    {...TransitionProps}
                    style={{
                      transformOrigin:
                        placement === 'bottom' ? 'right top' : 'right bottom'
                    }}
                  >
                    <div>
                      <ClickAwayListener onClickAway={handleCloseDecline}>
                        <div className="w-42 flex flex-col gap-2 rounded-lg bg-white p-3 shadow">
                          <div
                            onClick={() => authorizeProcess(true)}
                            className="flex cursor-pointer items-center gap-2 rounded p-2 hover:bg-grey-6"
                          >
                            <EditIcon />
                            <p>
                              {t('process_details_page.authorize_decline_edit')}
                            </p>
                          </div>
                          <div
                            onClick={() => unauthorizeProcess()}
                            className="flex cursor-pointer items-center gap-2 rounded p-2 hover:bg-grey-6"
                          >
                            <DeclinedIcon className="size-5" />
                            <p>
                              {t(
                                'process_details_page.authorize_decline_cancel'
                              )}
                            </p>
                          </div>
                        </div>
                      </ClickAwayListener>
                    </div>
                  </Grow>
                )}
              </Popper>
            </div>
          </div>
        )}
      </div>

      <RequestDocumentsModal
        isOpen={showRequestDocsModal}
        onRequestClose={() => setShowRequestDocsModal(false)}
        processId={id ?? ''}
      />
    </>
  )
}
export default AuthorizeBanner
